import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Target, Utensils } from 'lucide-react';
import { Button } from '@/components/ui';

type FoodType = 'fish' | 'crab' | 'shrimp' | 'squid' | 'urchin' | 'clam';

interface Tile {
  id: string;
  type: FoodType;
  row: number;
  col: number;
  selected: boolean;
  matched: boolean;
}

interface FeedingGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

const GRID_SIZE = 6;
const TARGET_CALORIES = 500;
const FOOD_TYPES: FoodType[] = ['fish', 'crab', 'shrimp', 'squid', 'urchin', 'clam'];

const FOOD_ICONS: Record<FoodType, string> = {
  fish: '🐟',
  crab: '🦀',
  shrimp: '🦐',
  squid: '🦑',
  urchin: '🟣',
  clam: '🐚',
};

const FOOD_POINTS: Record<FoodType, number> = {
  fish: 20,
  crab: 15,
  shrimp: 10,
  squid: 15,
  urchin: 25,
  clam: 12,
};

const PREFERRED_FOOD: Record<string, FoodType[]> = {
  'sea-otter': ['urchin', 'clam', 'crab'],
  'giant-otter': ['fish'],
  'eurasian-otter': ['fish', 'crab'],
  'asian-small-clawed-otter': ['crab', 'shrimp', 'clam'],
  'smooth-coated-otter': ['fish', 'shrimp'],
  'north-american-river-otter': ['fish', 'crab'],
  'spotted-necked-otter': ['fish'],
  'african-clawless-otter': ['crab', 'clam'],
  'marine-otter': ['urchin', 'crab', 'shrimp'],
  'hairy-nosed-otter': ['fish', 'crab'],
  'neotropical-otter': ['fish', 'crab'],
  'southern-river-otter': ['fish', 'crab'],
  'congo-clawless-otter': ['fish', 'crab'],
};

function generateGrid(): Tile[] {
  const tiles: Tile[] = [];
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      tiles.push({
        id: `${row}-${col}`,
        type: FOOD_TYPES[Math.floor(Math.random() * FOOD_TYPES.length)],
        row,
        col,
        selected: false,
        matched: false,
      });
    }
  }
  return tiles;
}

function findMatches(tiles: Tile[]): Set<string> {
  const matches = new Set<string>();

  // Check horizontal matches
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE - 2; col++) {
      const t1 = tiles.find(t => t.row === row && t.col === col);
      const t2 = tiles.find(t => t.row === row && t.col === col + 1);
      const t3 = tiles.find(t => t.row === row && t.col === col + 2);

      if (t1 && t2 && t3 && t1.type === t2.type && t2.type === t3.type && !t1.matched && !t2.matched && !t3.matched) {
        matches.add(t1.id);
        matches.add(t2.id);
        matches.add(t3.id);
      }
    }
  }

  // Check vertical matches
  for (let col = 0; col < GRID_SIZE; col++) {
    for (let row = 0; row < GRID_SIZE - 2; row++) {
      const t1 = tiles.find(t => t.row === row && t.col === col);
      const t2 = tiles.find(t => t.row === row + 1 && t.col === col);
      const t3 = tiles.find(t => t.row === row + 2 && t.col === col);

      if (t1 && t2 && t3 && t1.type === t2.type && t2.type === t3.type && !t1.matched && !t2.matched && !t3.matched) {
        matches.add(t1.id);
        matches.add(t2.id);
        matches.add(t3.id);
      }
    }
  }

  return matches;
}

export function FeedingGame({ onComplete, onBack, speciesId, otterName }: FeedingGameProps) {
  const [tiles, setTiles] = useState<Tile[]>(() => generateGrid());
  const [selectedTile, setSelectedTile] = useState<Tile | null>(null);
  const [calories, setCalories] = useState(0);
  const [movesLeft, setMovesLeft] = useState(20);
  const [isComplete, setIsComplete] = useState(false);
  const [combo, setCombo] = useState(0);
  const [showCombo, setShowCombo] = useState(false);

  const preferredFoods = PREFERRED_FOOD[speciesId] || ['fish'];

  // Check for matches after tiles change
  useEffect(() => {
    const checkMatches = () => {
      const matches = findMatches(tiles);

      if (matches.size >= 3) {
        setShowCombo(true);
        setTimeout(() => setShowCombo(false), 500);

        // Calculate points
        let points = 0;
        const matchedTypes = new Set<FoodType>();

        matches.forEach(id => {
          const tile = tiles.find(t => t.id === id);
          if (tile) {
            matchedTypes.add(tile.type);
            let tilePoints = FOOD_POINTS[tile.type];
            if (preferredFoods.includes(tile.type)) {
              tilePoints *= 1.5;
            }
            points += tilePoints;
          }
        });

        // Combo bonus
        const comboBonus = 1 + combo * 0.1;
        points = Math.floor(points * comboBonus);

        setCalories(prev => prev + points);
        setCombo(prev => prev + 1);

        // Mark matched tiles
        setTimeout(() => {
          setTiles(prev => {
            const newTiles = prev.map(t =>
              matches.has(t.id) ? { ...t, matched: true } : t
            );

            // Drop tiles down
            setTimeout(() => {
              setTiles(curr => {
                const dropped = [...curr];

                for (let col = 0; col < GRID_SIZE; col++) {
                  const colTiles = dropped
                    .filter(t => t.col === col && !t.matched)
                    .sort((a, b) => b.row - a.row);

                  let nextRow = GRID_SIZE - 1;
                  colTiles.forEach(tile => {
                    tile.row = nextRow--;
                  });

                  // Add new tiles at top
                  const matchedCount = GRID_SIZE - colTiles.length;
                  for (let i = 0; i < matchedCount; i++) {
                    dropped.push({
                      id: `new-${Date.now()}-${col}-${i}`,
                      type: FOOD_TYPES[Math.floor(Math.random() * FOOD_TYPES.length)],
                      row: i,
                      col,
                      selected: false,
                      matched: false,
                    });
                  }
                }

                return dropped.filter(t => !t.matched);
              });
            }, 200);

            return newTiles;
          });
        }, 100);
      } else {
        // No matches, reset combo
        if (combo > 0) {
          setCombo(0);
        }
      }
    };

    const timer = setTimeout(checkMatches, 100);
    return () => clearTimeout(timer);
  }, [tiles, combo, preferredFoods]);

  // Check win/lose condition
  useEffect(() => {
    if (calories >= TARGET_CALORIES && !isComplete) {
      setIsComplete(true);
      const stars = movesLeft > 10 ? 3 : movesLeft > 5 ? 2 : 1;
      setTimeout(() => onComplete(calories, stars), 1500);
    } else if (movesLeft <= 0 && calories < TARGET_CALORIES && !isComplete) {
      setIsComplete(true);
      setTimeout(() => onComplete(calories, 0), 1500);
    }
  }, [calories, movesLeft, isComplete, onComplete]);

  const handleTileClick = useCallback((tile: Tile) => {
    if (isComplete || tile.matched) return;

    if (!selectedTile) {
      setSelectedTile(tile);
      setTiles(prev => prev.map(t =>
        t.id === tile.id ? { ...t, selected: true } : t
      ));
    } else {
      // Check if adjacent
      const rowDiff = Math.abs(selectedTile.row - tile.row);
      const colDiff = Math.abs(selectedTile.col - tile.col);
      const isAdjacent = (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);

      if (isAdjacent) {
        // Swap tiles
        setTiles(prev => {
          const newTiles = prev.map(t => {
            if (t.id === selectedTile.id) {
              return { ...t, row: tile.row, col: tile.col, selected: false };
            }
            if (t.id === tile.id) {
              return { ...t, row: selectedTile.row, col: selectedTile.col };
            }
            return { ...t, selected: false };
          });
          return newTiles;
        });
        setMovesLeft(prev => prev - 1);
      } else {
        // Deselect
        setTiles(prev => prev.map(t => ({ ...t, selected: false })));
      }
      setSelectedTile(null);
    }
  }, [selectedTile, isComplete]);

  const progressPercent = Math.min(100, (calories / TARGET_CALORIES) * 100);

  return (
    <div className="fixed inset-0 bg-otter-900 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Feeding Time</h2>
          <div className="text-white font-bold">{movesLeft} moves</div>
        </div>

        {/* Calorie progress */}
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-orange-400" />
          <div className="flex-1 h-4 bg-black/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-orange-500 to-yellow-500"
              animate={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-white text-sm font-bold">
            {calories}/{TARGET_CALORIES}
          </span>
        </div>

        {/* Preferred food hint */}
        <div className="flex items-center gap-2 text-sm text-white/60">
          <Utensils className="w-3 h-3" />
          <span>{otterName} loves: </span>
          {preferredFoods.map(food => (
            <span key={food} className="text-lg">{FOOD_ICONS[food]}</span>
          ))}
          <span className="text-green-400 ml-1">(1.5x points!)</span>
        </div>
      </div>

      {/* Game grid */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div
          className="grid gap-1 bg-black/20 p-2 rounded-xl"
          style={{
            gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
            width: 'min(90vw, 360px)',
            aspectRatio: '1',
          }}
        >
          {tiles.filter(t => !t.matched).map(tile => (
            <motion.button
              key={tile.id}
              layoutId={tile.id}
              onClick={() => handleTileClick(tile)}
              className={`
                aspect-square rounded-lg text-2xl flex items-center justify-center
                transition-all duration-150
                ${tile.selected
                  ? 'bg-yellow-500 ring-2 ring-yellow-300 scale-110'
                  : preferredFoods.includes(tile.type)
                    ? 'bg-green-700/50 hover:bg-green-600/50'
                    : 'bg-otter-700/50 hover:bg-otter-600/50'
                }
              `}
              style={{
                gridRow: tile.row + 1,
                gridColumn: tile.col + 1,
              }}
              animate={{
                scale: tile.selected ? 1.1 : 1,
              }}
              whileTap={{ scale: 0.95 }}
            >
              {FOOD_ICONS[tile.type]}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Combo indicator */}
      <AnimatePresence>
        {showCombo && combo > 1 && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          >
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-full px-6 py-2">
              <span className="text-white font-bold text-xl">
                x{combo} COMBO!
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion overlay */}
      <AnimatePresence>
        {isComplete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/70 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`
                rounded-2xl p-6 text-center
                ${calories >= TARGET_CALORIES ? 'bg-green-600' : 'bg-orange-600'}
              `}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                {calories >= TARGET_CALORIES ? 'Well Fed!' : 'Time\'s Up!'}
              </h3>
              <p className="text-white/80 mb-3">
                {calories >= TARGET_CALORIES
                  ? `${otterName} ate ${calories} calories!`
                  : `${otterName} only got ${calories}/${TARGET_CALORIES} calories.`
                }
              </p>
              <div className="flex justify-center gap-1">
                {[1, 2, 3].map(i => (
                  <Star
                    key={i}
                    className={`w-8 h-8 ${
                      (calories >= TARGET_CALORIES && (movesLeft > 10 ? i <= 3 : movesLeft > 5 ? i <= 2 : i <= 1))
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions */}
      <div className="p-4 bg-black/30">
        <p className="text-white/50 text-xs text-center">
          Swap adjacent foods to match 3 or more! Preferred foods = bonus points
        </p>
      </div>
    </div>
  );
}
